import type { TaskPriority, TaskStatus } from '@/types/database';

/**
 * Shared display metadata for the Tasks section surfaces (My tasks page,
 * detail page list + mobile cards, calendar). Brand accents are used
 * strictly as wayfinding — chips, dots, accent bars — never as large
 * ambient fills (BRAND.md).
 */

export interface PriorityMeta {
  label: string;
  /** Chip fill + text colour. */
  className: string;
  /** Solid swatch for dots and card accent bars. */
  dot: string;
  order: number;
}

export const PRIORITY_META: Record<TaskPriority, PriorityMeta> = {
  low: {
    label: 'Low',
    className: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/40',
    order: 0,
  },
  medium: {
    label: 'Medium',
    className: 'bg-atmosphere/40 text-atmosphere-dark',
    dot: 'bg-atmosphere-dark',
    order: 1,
  },
  high: {
    label: 'High',
    className: 'bg-fresh-salmon/40 text-fresh-salmon-dark',
    dot: 'bg-fresh-salmon',
    order: 2,
  },
  super_high: {
    label: 'Super High',
    className: 'bg-fresh-salmon-dark text-linen',
    dot: 'bg-fresh-salmon-dark',
    order: 3,
  },
};

export interface StatusMeta {
  label: string;
  className: string;
  dot: string;
}

export const STATUS_META: Record<TaskStatus, StatusMeta> = {
  // "Not started" is the default for brand-new tasks. A dashed outline
  // + transparent fill reads as "untouched" against the solid badges
  // used by the active workflow states.
  not_started: {
    label: 'Not started',
    className:
      'border border-dashed border-border bg-transparent text-muted-foreground',
    dot: 'border border-dashed border-muted-foreground bg-transparent',
  },
  waiting: {
    label: 'Waiting',
    className: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/60',
  },
  in_progress: {
    label: 'In progress',
    className: 'bg-atmosphere-dark text-linen',
    dot: 'bg-atmosphere-dark',
  },
};

export const PRIORITY_ORDER: TaskPriority[] = [
  'low',
  'medium',
  'high',
  'super_high',
];

export const STATUS_ORDER: TaskStatus[] = ['not_started', 'waiting', 'in_progress'];

/**
 * "Completed" is offered in the status dropdown but is NOT a `task_status`
 * enum value — it is the Asana-style checkmark (`tasks.is_completed`)
 * surfaced as a status option. A completed task reads as "Completed"
 * regardless of its stored workflow status, and picking "Completed" in
 * the dropdown does exactly what ticking the checkbox does.
 */
export const COMPLETED_STATUS_OPTION = 'completed' as const;

export type TaskStatusOption = TaskStatus | typeof COMPLETED_STATUS_OPTION;

export const COMPLETED_STATUS_META: StatusMeta = {
  label: 'Completed',
  className: 'bg-bright-moss-dark text-linen',
  dot: 'bg-bright-moss-dark',
};

/** Dropdown order: workflow states first, then the terminal "Completed". */
export const STATUS_OPTION_ORDER: TaskStatusOption[] = [
  ...STATUS_ORDER,
  COMPLETED_STATUS_OPTION,
];

export function statusOptionMeta(option: TaskStatusOption): StatusMeta {
  return option === COMPLETED_STATUS_OPTION
    ? COMPLETED_STATUS_META
    : STATUS_META[option];
}

/** What the status dropdown should show for a task row. */
export function taskStatusOption(task: {
  status: TaskStatus;
  is_completed: boolean;
}): TaskStatusOption {
  return task.is_completed ? COMPLETED_STATUS_OPTION : task.status;
}

/**
 * Translate a status-dropdown pick into a `tasks` patch.
 *
 *   * "Completed"                   → tick the checkmark (status untouched,
 *                                     so un-ticking later restores it)
 *   * workflow state, task is done  → un-tick + set the new status, so the
 *                                     dropdown reflects the pick instead
 *                                     of snapping back to "Completed"
 *   * workflow state, task is open  → plain status change
 */
export function taskStatusPatch(
  task: { status: TaskStatus; is_completed: boolean },
  next: TaskStatusOption,
): { status?: TaskStatus; is_completed?: boolean } {
  if (next === COMPLETED_STATUS_OPTION) return { is_completed: true };
  if (task.is_completed) return { is_completed: false, status: next };
  return { status: next };
}

export type DueState = 'overdue' | 'today' | 'upcoming' | 'none';

/**
 * Compare a YYYY-MM-DD string to "today" in the local timezone.
 * `new Date('2026-01-05')` parses as midnight UTC, which lands on the
 * previous day in negative-UTC zones, so the parts are rebuilt as a
 * local-midnight Date before comparing.
 */
export function dueState(due: string | null | undefined): DueState {
  if (!due) return 'none';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const parts = due
    .slice(0, 10)
    .split('-')
    .map((s) => parseInt(s, 10));
  const dueDate = new Date(parts[0]!, (parts[1] ?? 1) - 1, parts[2] ?? 1);
  if (dueDate.getTime() < today.getTime()) return 'overdue';
  if (dueDate.getTime() === today.getTime()) return 'today';
  return 'upcoming';
}

const DUE_SHORT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});
const DUE_SHORT_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** "Oct 14" (or "Oct 14, 2027" outside the current year) for a YYYY-MM-DD date. */
export function formatDueShort(due: string): string {
  const [y, m, d] = due.slice(0, 10).split('-').map(Number);
  const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return date.getFullYear() === new Date().getFullYear()
    ? DUE_SHORT.format(date)
    : DUE_SHORT_YEAR.format(date);
}

/** Chip tone for a due date, keyed by `dueState`. */
export const DUE_TONE: Record<DueState, string> = {
  overdue: 'bg-fresh-salmon/25 text-fresh-salmon-dark',
  today: 'bg-info-soft/40 text-info',
  upcoming: 'bg-secondary text-muted-foreground',
  none: 'bg-transparent text-muted-foreground',
};

/**
 * Brand colourways for the project monogram tile. Fresh Salmon is left
 * out on purpose — it carries the "warning / overdue" meaning and a
 * project's identity colour shouldn't read as an alert. Index
 * stability matters: the slot is derived from the project id so a
 * project keeps its colour across sessions.
 */
const PROJECT_ACCENTS = [
  'bg-humus text-bright-moss',
  'bg-old-growth text-linen',
  'bg-atmosphere-dark text-atmosphere',
  'bg-bright-moss-dark text-bright-moss',
  'bg-old-growth-dark text-bright-moss',
  'bg-linen text-humus',
] as const;

export function projectAccent(id: string): string {
  // FNV-1a — spreads short and UUID-shaped ids evenly across the slots.
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return PROJECT_ACCENTS[(hash >>> 0) % PROJECT_ACCENTS.length]!;
}

export function projectInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

export function completionPercent(total: number, open: number): number {
  if (total <= 0) return 0;
  return Math.round(((total - open) / total) * 100);
}
